import { PublishingPost } from '~/post/types.generated';
import {
  RpcUpdate,
  SentPostActionLabel,
  SentPostActionType,
} from '~/post/types';
import { PostEntity } from '~/post/PostEntity';
import { SERVICE_YOUTUBE } from '~/constants';
import {
  isValidSentPostAction,
  reduceSentPostActions,
  useSentPostActionsResponse,
} from './helpers';
import { BaseSentPostAction, SentPostAction } from './types';

export type useSentPostActionsProps = {
  post: PublishingPost | RpcUpdate | undefined;
  onShareLinkClick?: (args?: {
    post?: PublishingPost | RpcUpdate;
    selectedProfileIds?: string[];
  }) => void;
  onShareAgainClick?: (args?: { post?: PublishingPost | RpcUpdate }) => void;
  onViewPostClick?: (args?: unknown) => void;
};

/**
 * Return an object containing arrays of valid sent post actions
 * that can be performed in the given context.
 *
 * It will exclude actions based on the conditions provided below per action,
 * as well as whether a callback for the given action has been provided.
 *
 * The provided post is validated and the validation is used when calculating
 * valid posts.
 *
 * @returns {
 *    stackedActions: An array of SentPostActions that will be rendered in a single, stacked Select Button
 * }
 */

export const useSentPostActions = ({
  post,
  onShareLinkClick,
  onShareAgainClick,
  onViewPostClick,
}: useSentPostActionsProps): useSentPostActionsResponse => {
  const isSent = PostEntity.isSent(post);
  const channel = PostEntity.getChannel(post);
  const isYoutubeChannel = channel?.name === SERVICE_YOUTUBE;

  const sentPostActions: BaseSentPostAction[] = [
    {
      id: SentPostActionType.SHARE_LINK,
      title: SentPostActionLabel.SHARE_LINK,
      callback: onShareLinkClick,
      condition: isSent && isYoutubeChannel,
    },
    {
      id: SentPostActionType.SHARE_AGAIN,
      title: SentPostActionLabel.SHARE_AGAIN,
      callback: onShareAgainClick,
      condition: isSent && isYoutubeChannel,
    },
    {
      id: SentPostActionType.VIEW_POST,
      title: SentPostActionLabel.VIEW_POST,
      callback: onViewPostClick,
      condition: isSent && isYoutubeChannel,
    },
  ];

  return sentPostActions
    .filter(isValidSentPostAction)
    .reduce(reduceSentPostActions, {
      stackedActions: [] as SentPostAction[],
    });
};
